/**
 * Fallback CSS-path walker — the safety net for the v1 element-selector
 * algorithm.
 *
 * When the orchestrator's strategy chain finds nothing usable across the full
 * ancestor walk, the engine invokes this fallback. It produces a positional
 * selector by walking up from the target to <html>, expressing each step as
 * either an id anchor (when one survives the autogen filter) or a
 * `tag:nth-of-type(n)` step.
 *
 * Key differences from the legacy autocapture `cssPath`:
 *
 *   1. Ids are filtered through `getStableId`, which honors the
 *      explicit-tracking-attribute suppression signal AND the autogenerated-id
 *      pattern pack. The legacy `cssPath` would happily anchor on `id=":r5:"`
 *      and produce selectors that never match across page loads.
 *
 *   2. Classes — when they're used at all for sibling disambiguation — are
 *      filtered through `filterClasses` against the unstable-class pattern
 *      pack. The current v1 implementation here doesn't emit classes (we lean
 *      entirely on `:nth-of-type` for sibling disambiguation), but the
 *      filtering helper is wired through so future iterations can opt back in
 *      surgically without re-deriving the policy.
 *
 *   3. A unique anchor id terminates the walk early — once we hit `body#main`,
 *      we don't need to keep walking up to `<html>`.
 *
 *   4. Ambiguous id anchors are skipped. Duplicate ids are invalid HTML but
 *      common enough in the wild that fallback must not emit selectors that
 *      resolve to the wrong target.
 *
 * Output format mirrors the orchestrator's output: `<anchor> > <descent>`.
 * When no usable id is found anywhere on the walk, the fallback emits a
 * pure-positional selector rooted at `html`.
 *
 * See the design doc:
 *   packages/plugin-autocapture-browser/element-selector-strategy-v1-no-classes.md
 */

import { ResolvedSelectorConfig } from './types';
import { getStableId } from './helpers/get-stable-id';
import { escapeIdForCss } from './helpers/escape-id';

export interface FallbackCssPathOptions {
  /** Document or shadow root used for uniqueness checks. Defaults to the target's owner document. */
  scope?: ParentNode;
}

/**
 * Build a positional CSS selector for `el`, using stable ids as anchors when
 * available and falling back to `tag:nth-of-type(n)` for disambiguation.
 *
 * Honors `config.maxAncestorWalkDepth` defensively — once the depth limit is
 * reached, the walker stops and returns whatever it has built so far rooted at
 * the deepest reached ancestor.
 */
export function fallbackCssPath(
  el: Element,
  config: ResolvedSelectorConfig,
  options: FallbackCssPathOptions = {},
): string {
  const scope: ParentNode = options.scope ?? el.ownerDocument ?? document;
  const segments: string[] = [];
  let cursor: Element | null = el;
  let depth = 0;

  while (cursor !== null) {
    if (config.maxAncestorWalkDepth !== undefined && depth > config.maxAncestorWalkDepth) {
      break;
    }

    const id = getStableId(cursor, config);
    if (id !== null) {
      // Anchor format mirrors the stableId strategy: `tag#id` with the id
      // CSS-escaped. Only terminate if the composed selector uniquely resolves
      // to the target; duplicate ids should fall through to positional steps.
      const anchorSegment = `${cursor.tagName.toLowerCase()}#${escapeIdForCss(id)}`;
      const candidate = [anchorSegment, ...segments].join(' > ');
      if (isUniqueMatch(scope, candidate, el)) {
        return candidate;
      }
    }

    segments.unshift(stepFor(cursor));
    cursor = cursor.parentElement;
    depth += 1;
  }

  // Walked all the way to the document root without finding an id. The first
  // segment is whatever `<html>` produced (which is just `html` since it has
  // no parent and no same-tag siblings).
  return segments.join(' > ');
}

/**
 * Build a single step. For an element with a parent: `tag:nth-of-type(n)`.
 * For a root element (no parent — i.e. <html>): just `tag`.
 *
 * Class-based disambiguation is intentionally not emitted in v1 — see the
 * design doc, "Why we don't use classes for sibling disambiguation". The
 * `filterClasses` helper is imported in the package for future iterations.
 */
function stepFor(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const parent = el.parentElement;
  if (parent === null) {
    return tag;
  }
  const index = sameTypeIndex(el, parent);
  return `${tag}:nth-of-type(${index})`;
}

function sameTypeIndex(el: Element, parent: Element): number {
  let count = 0;
  for (let i = 0; i < parent.children.length; i++) {
    const sibling = parent.children[i];
    if (sibling.tagName === el.tagName) {
      count += 1;
      if (sibling === el) return count;
    }
  }
  return 1;
}

function isUniqueMatch(scope: ParentNode, selector: string, el: Element): boolean {
  let matches: NodeListOf<Element>;
  try {
    matches = scope.querySelectorAll(selector);
  } catch (_e) {
    return false;
  }
  return matches.length === 1 && matches[0] === el;
}
